import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa"; // Import Eye icons from react-icons
import "../../styles/HomeStyling/ChangePassword.css";

export const ChangePassword = ({ email, setShowChangePassword, onBack }) => {
  const Back = `${process.env.PUBLIC_URL}/assets/logos/back.svg`;
  const navigate = useNavigate();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [passwordStrength, setPasswordStrength] = useState("Weak ❌");

  // Define the five constraints
  const constraints = useMemo(
    () => ({
      minLength: newPassword.length >= 8,
      hasUpper: /[A-Z]/.test(newPassword),
      hasLower: /[a-z]/.test(newPassword),
      hasNumber: /\d/.test(newPassword),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
    }),
    [newPassword]
  );

  // Count how many constraints are met
  const constraintsMetCount = useMemo(() => {
    return Object.values(constraints).filter(Boolean).length;
  }, [constraints]);

  // Combined effect: validates constraints and confirm password together.
  useEffect(() => {
    // Check if confirmPassword is non-empty and matches newPassword
    if (
      oldPassword.length > 0 &&
      newPassword.length > 0 &&
      confirmPassword.length > 0 &&
      confirmPassword !== newPassword
    ) {
      setError("Passwords do not match.");
    } else {
      setError("");
    }

    // Set password strength based solely on the constraints
    if (constraintsMetCount < 3) {
      setPasswordStrength("Weak ❌");
    } else if (constraintsMetCount < 5) {
      setPasswordStrength("Medium ⚠️");
    } else {
      setPasswordStrength("Strong ✅");
    }

    // Enable submit only if:
    // - At least 3 constraints are met
    // - Confirm password is filled and matches new password
    if (
      constraintsMetCount >= 3 &&
      confirmPassword &&
      confirmPassword === newPassword
    ) {
      setIsSubmitDisabled(false);
    } else {
      setIsSubmitDisabled(true);
    }
  }, [constraintsMetCount, newPassword, confirmPassword]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/set_new_password`,
        {
          email,
          old_password: oldPassword,
          new_password: newPassword,
        }
      );
      if (response.data.success) {
        setSuccessMessage("Password updated successfully! Redirecting...");
        setError("");
        setTimeout(() => {
          setShowChangePassword(false);
          navigate("/dashboard");
        }, 2000);
      } else {
        setError(response.data.message || "Failed to update password.");
      }
    } catch (err) {
      setError(
        err.response?.data?.message || "An error occurred. Please try again."
      );
    }
  };

  return (
    <form className="change-password-form" onSubmit={handleChangePassword}>
      <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              width: "fit-content",
              borderRadius: "50px",
              backgroundColor: "#eaeaea",
              padding: "10px",
            }}
          >
            <img src={Back} alt="Back to Login" onClick={onBack} />
      </div>
      <h4 className="login-title">Change Password</h4>
      <div className="message-container">
        {error && <p className="error-message">{error}</p>}
        {successMessage && <p className="success-message">{successMessage}</p>}
      </div>
      <div className="change-password-form-group">
        <label className="login-label">Old Password</label>
        <div className="login-input-container">
          <input
            type={showOldPassword ? "text" : "password"}
            className="login-input"
            placeholder="Enter Old Password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            required
          />
          {oldPassword.length !== 0 && (
            <span
              className="eye-icon"
              onClick={() => setShowOldPassword((prev) => !prev)}
              aria-label="Toggle Password Visibility"
            >
              {showOldPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          )}
        </div>
      </div>
      <div className="change-password-form-group">
        <div className="password-group">
          <label className="login-label">New Password</label>
          {newPassword.length !== 0 && (
            <small className="password-strength">{passwordStrength}</small>
          )}
        </div>
        <div className="login-input-container">
          <input
            type={showNewPassword ? "text" : "password"}
            className="login-input"
            value={newPassword}
            placeholder="Enter New Password"
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          {newPassword.length !== 0 && (
            <span
              className="eye-icon"
              onClick={() => setShowNewPassword((prev) => !prev)}
              aria-label="Toggle Password Visibility"
            >
              {showNewPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          )}
        </div>
        <div className="constraints-list">
          <p
            className={
              constraints.minLength ? "constraint met" : "constraint unmet"
            }
          >
            ✔ Minimum 8 characters
          </p>
          <p
            className={
              constraints.hasUpper ? "constraint met" : "constraint unmet"
            }
          >
            ✔ At least 1 uppercase letter
          </p>
          <p
            className={
              constraints.hasLower ? "constraint met" : "constraint unmet"
            }
          >
            ✔ At least 1 lowercase letter
          </p>
          <p
            className={
              constraints.hasNumber ? "constraint met" : "constraint unmet"
            }
          >
            ✔ At least 1 number
          </p>
          <p
            className={
              constraints.hasSpecial ? "constraint met" : "constraint unmet"
            }
          >
            ✔ At least 1 special character
          </p>
        </div>
      </div>
      <div className="change-password-form-group">
        <label className="login-label">Confirm Password</label>
        <div className="login-input-container">
          <input
            type={showConfirmPassword ? "text" : "password"}
            className="login-input"
            value={confirmPassword}
            placeholder="Confirm New Password"
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
          {confirmPassword.length !== 0 && (
            <span
              className="eye-icon"
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              aria-label="Toggle Password Visibility"
            >
              {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          )}
        </div>
      </div>
      <div className="action-buttons">
        <button
          type="submit"
          className="submit-btn"
          disabled={isSubmitDisabled}
        >
          Change Password
        </button>
      </div>
    </form>
  );
};
